import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Shield, 
  Users, 
  Building2, 
  GraduationCap, 
  Target, 
  UserCheck,
  CheckCircle2,
  ClipboardCheck,
  Package,
  FileCheck,
  ShieldCheck,
  FileSearch,
  ShieldAlert
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const services = [
  {
    icon: Shield,
    title: "Fractional CISO Services",
    description: "Strategic cyber security leadership on a contract basis. Get C-level security expertise without the full-time cost.",
    url: "/services/fractional-ciso-uk",
  },
  {
    icon: Package,
    title: "Risk Management",
    description: "Identify, assess, and mitigate cyber risks to protect your organisation's critical assets.",
    url: "/services/risk-management",
  },
  {
    icon: CheckCircle2,
    title: "Cyber Essentials Certification",
    description: "Get certified in 4-6 weeks with expert support. Required for UK government contracts. 98% pass rate.",
    url: "/services/cyber-essentials-certification-uk",
  },
  {
    icon: ClipboardCheck,
    title: "Cyber Essentials Readiness",
    description: "Free readiness assessment. Check if you're ready to certify and identify gaps before certification.",
    url: "/services/cyber-essentials-readiness",
  },
  {
    icon: Target,
    title: "Security Architecture",
    description: "Design robust security frameworks tailored to your business needs and risk profile.",
    url: "/services/architecture",
  },
  {
    icon: Building2,
    title: "Green-field Builds",
    description: "Build security into your infrastructure from day one. Design and implement secure systems from the ground up.",
    url: "/services/greenfield",
  },
  {
    icon: Users,
    title: "Outsourcing",
    description: "Flexible security team augmentation. Scale your security capabilities as needed.",
    url: "/services/outsourcing",
  },
  {
    icon: GraduationCap,
    title: "Training & Awareness",
    description: "Empower your team with security knowledge. Build a security-conscious culture.",
    url: "/services/training",
  },
  {
    icon: UserCheck,
    title: "Strategy & Recruitment",
    description: "Develop comprehensive security strategies and find the right talent for your team.",
    url: "/services/strategy",
  },
  {
    icon: ShieldCheck,
    title: "Resilience Products",
    description: "Implement tools and solutions that strengthen your security posture and resilience.",
    url: "/services/resilience",
  },
  {
    icon: FileCheck,
    title: "NIST Assessments",
    description: "Comprehensive assessments against NIST Cyber Security Framework to strengthen your security posture.",
    url: "/services/nist-assessments",
  },
  {
    icon: FileSearch,
    title: "CAF Assessments",
    description: "Evaluate your cyber security maturity against the NCSC Cyber Assessment Framework.",
    url: "/services/caf-assessments",
  },
  {
    icon: ShieldAlert,
    title: "Business Continuity and Incident Response Planning",
    description: "Prepare for and respond to security incidents with comprehensive business continuity and incident response planning.",
    url: "/services/crisis-management",
  },
];

const Services = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Security Services - Fractional CISO, Risk Management & More | 4FOX Security"
        description="Comprehensive cyber security services including fractional CISO, risk assessments, Cyber Essentials certification, incident response planning, and security training for UK businesses."
        canonical="https://4fox.uk/services"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Cyber Security Consulting",
          "provider": {
            "@type": "Organization",
            "name": "4FOX Security"
          }
        }}
      />
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Comprehensive Security Services
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From strategic fractional CISO support to hands-on implementation, 
              we provide the full spectrum of cyber security services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link key={index} to={service.url}>
                  <Card 
                    className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-accent hover:border-2 cursor-pointer h-full"
                  >
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                        <Icon className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
